// Sentry lifecycle, gated on cookie consent.
//
// Single public function: syncConsentToSentry({ analytics }).
// Initializes the Sentry SDK the first time analytics consent is
// granted, and tears it down via Sentry.close() when consent is
// withdrawn. Idempotent — safe to call from a useEffect on every
// consent change without per-call bookkeeping.
//
// Placement rationale: Sentry's session replay starts recording at
// init() time and the browser-tracing integration installs a global
// fetch wrapper. Both produce data we can't legally collect under the
// "Analize" category until the user agrees, so init() must NOT run at
// module top-level (the previous shape pre-dated the consent layer).
//
// Driven by ConsentContext via the same useEffect that pushes consent
// into gtag, so the flip from denied → granted reaches both vendors in
// the same React commit.

import * as Sentry from '@sentry/react';
import { isNativeApp, isBusinessApp } from '../platform';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Release identity. Deliberately NOT one build-time constant shared by every
// target, which is what this used to be.
//
// It read package.json's version, which sat at 1.0.0 while three real binaries
// shipped past it — iOS client 1.0.1(26), Android client 1.0.2(4), Android
// business 1.0.1(3) — so every one of them reported `petzy-client@1.0.0` and
// collapsed into a single bucket. One JS bundle ships to BOTH stores at
// DIFFERENT versions, so no value baked at `vite build` time can ever be right
// for both; the old constant wasn't stale, it was unfixable in that shape.
//
// The damage isn't cosmetic. Sentry's "resolve in next release" compares the
// resolved-in release against the incoming one; when they are always equal, a
// regression can never re-open. You mark a crash fixed, ship the next build,
// users keep crashing, and the issue sits there Resolved with no alert — which
// is exactly the resolve-and-reship loop the iOS app is in right now.
//
// So: native reads the version off the actual binary at runtime (see
// initNativeSentry), and web — which has no binary and deploys continuously —
// uses the commit SHA (see vite.config.js). The native source of truth is the
// one step that CANNOT be forgotten: both stores reject an upload that reuses a
// version+build, so Apple and Google enforce the bump for us. There is no third
// file to keep in sync.
const SENTRY_WEB_RELEASE = `petzy-web@${import.meta.env.VITE_COMMIT_SHA || 'dev'}`;

// `environment` is the deployment STAGE — never the product. It used to be
// import.meta.env.MODE, which emits "business" for `vite build --mode business`:
// a product name in a stage field, which left NO tag on any event carrying the
// stage at all, so a rule separating real traffic from a laptop was impossible
// to write. Product identity lives in the release's package component
// (petzy-client / petzy-business / petzy-web), where it already was.
const SENTRY_ENVIRONMENT = import.meta.env.PROD ? 'production' : 'development';

// Native (Capacitor) consent gate, read by beforeSend at event time.
// A module-level flag (not a Sentry tag) so the drop decision is
// synchronous and can't race the async init/close lifecycle: revoke
// flips this false immediately, and any event still in flight through
// the SDK pipeline is dropped even before close() lands.
let nativeConsentGranted = false;

// Strip obvious PII from breadcrumb messages before they leave the
// device: emails and credential-looking `token=…` pairs picked up from
// console logs or XHR/fetch breadcrumbs. Defensive — the native config
// below already disables PII capture; this catches accidental leaks in
// our own log strings so diagnostics stay "Not Linked to User".
function sanitizeBreadcrumbMessage(message) {
  if (!message || typeof message !== 'string') return message;
  return message
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]')
    // RFC 6750 form: "Bearer <token>" with a bare space, no [:=] — the
    // shape Authorization headers take in logged errors. Runs BEFORE the
    // generic rule so a nested '"token": "Bearer eyJ…"' collapses fully.
    // Space-separated matching stays bearer-ONLY: extending it to the
    // generic keywords would eat the next word of ordinary prose
    // ("token expired", "change password error"), gutting the
    // breadcrumb's diagnostic value for no real leak (our logs never
    // emit bare "token <value>").
    // (?![:=]) bails when the space is followed by a delimiter
    // ("bearer : t", "bearer = t", "bearer <tab>: t"): otherwise [^\s"'&]+
    // would swallow just the delimiter and leave the token exposed — the
    // generic [:=] rule below (which allows \s* before the delimiter)
    // redacts those forms fully instead.
    .replace(/\b(bearer)\s+(?![:=])[^\s"'&]+/gi, '$1:[redacted]')
    // Value = everything up to a natural delimiter (whitespace, quote,
    // &). An exclusion class instead of an allowlist of token alphabets:
    // a scrubber must over-redact, never leak a tail because some token
    // format used a character we didn't enumerate.
    .replace(/(bearer|token|auth|password|key|secret)["']?\s*[:=]\s*["']?[^\s"'&]+/gi, '$1:[redacted]');
}

// Native event scrubber: consent hard-gate + IP removal + breadcrumb
// sanitisation. Returning null drops the event before any network I/O.
function nativeBeforeSend(event) {
  if (!nativeConsentGranted) return null;
  if (event.user) delete event.user.ip_address;
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((b) => {
      if (b && b.category === 'console' && b.message) {
        return { ...b, message: sanitizeBreadcrumbMessage(b.message) };
      }
      // The SDK's automatic xhr/fetch breadcrumbs carry the request URL
      // in data.url (message is unset for them).
      if (b && (b.category === 'xhr' || b.category === 'fetch') && typeof b.data?.url === 'string') {
        return { ...b, data: { ...b.data, url: sanitizeBreadcrumbMessage(b.data.url) } };
      }
      return b;
    });
  }
  return event;
}

// Cached at module load — the DSN can't change between calls, so
// re-running `Boolean(... && trim().length > 0)` on every consent
// toggle is wasted work. The constant is the source of truth; the
// function is a thin logger wrapper that emits a one-time warning on
// the missing-env path so a misconfigured deploy is visible in the
// console without spamming on every consent change.
const HAS_DSN = Boolean(SENTRY_DSN && SENTRY_DSN.trim().length > 0);

// Dev machines do not report to the production project. The DSN lives in the
// local .env, so without this every `npm run dev` shipped localhost errors there
// — and, because replays sample at 10% (100% on error), a screen recording of
// the developer's session with them. PETZY-FRONTEND-C was exactly that: a
// ReferenceError from a module Vite had just hot-updated mid-keystroke, filed
// against release petzy-client@1.0.0 next to real user reports.
// VITE_SENTRY_DEBUG=1 opts back in for anyone verifying the consent gating or an
// integration locally.
const DEV_OPT_IN = import.meta.env.VITE_SENTRY_DEBUG === '1';
const ENV_ALLOWS_SENTRY = import.meta.env.PROD || DEV_OPT_IN;
const IS_CONFIGURED = HAS_DSN && ENV_ALLOWS_SENTRY;

let warnedMissingDsn = false;
export function isSentryConfigured() {
  if (!IS_CONFIGURED && !warnedMissingDsn) {
    // Two different reasons, two different messages — "DSN is not set" would be
    // a lie on a dev box that has one.
    console.warn(
      HAS_DSN
        ? '[Sentry] disabled in development so local errors and session replays stay out of the production project. Set VITE_SENTRY_DEBUG=1 to opt in.'
        : '[Sentry] VITE_SENTRY_DSN is not set — Sentry will not initialize. Set it in your .env / Vercel env to enable error tracking.'
    );
    warnedMissingDsn = true;
  }
  return IS_CONFIGURED;
}

let initialised = false;

// Wipe any persisted Sentry state. Mirrors clearGaCookies in gtag.js
// — Sentry.close() stops the SDK from emitting new events but does
// NOT remove the replay-session cookie or storage entries the SDK
// left behind. Cookiebot scans flag those as "tracker cookies set
// pre-consent" if they survive a revoke, which is exactly the
// failure mode this whole consent layer is here to prevent.
//
// Both sessionStorage AND localStorage are swept: the replay
// integration uses sessionStorage by default but switches to
// localStorage when configured with stickySession, and other Sentry
// integrations / future SDK versions may persist elsewhere. Prefix
// match on "sentry" covers all of them in one pass.
//
// Idempotent: deleting a cookie that was never set is a no-op the
// browser silently absorbs; storage.removeItem on a missing key is
// also a no-op. Safe to call whenever analytics is off, regardless of
// whether init ever ran.
function clearSentryStorage() {
  const host = window.location.hostname;
  const parts = host.split('.');
  const apex = parts.length > 2 ? parts.slice(-2).join('.') : null;
  const expire = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${host}`;
    if (apex && !host.endsWith('.vercel.app')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${apex}`;
    }
  };
  // Known Sentry SDK cookie name (Cookiebot-flagged). The replay
  // integration also persists buffer state under storage keys
  // prefixed with "sentry"; clear all of them so a stale buffer
  // doesn't survive into the next session.
  expire('sentryReplaySession');
  // Property access happens INSIDE the try because Safari (and some
  // privacy-mode configs) throw SecurityError on bare `sessionStorage`
  // / `localStorage` reads when storage is disallowed — evaluating
  // them in an outer expression would propagate before the catch
  // could run. Iterating the surface NAMES and resolving via
  // `window[name]` keeps the access inside the guarded block.
  ['sessionStorage', 'localStorage'].forEach((surface) => {
    try {
      const storage = window[surface];
      if (!storage) return;
      Object.keys(storage).forEach((k) => {
        if (k.toLowerCase().startsWith('sentry')) storage.removeItem(k);
      });
    } catch {
      // Storage disabled or SecurityError (incognito strict mode etc.)
      // — nothing to clean on this surface, treat as success and move
      // on to the next one.
    }
  });
}

// Native grant path: init @sentry/capacitor (JS errors + native-layer
// crashes on iOS/Android) with a data-minimised config so diagnostics
// stay "Not Linked to User" (Apple App Privacy) and match the Play
// Data Safety declaration (Crash logs + Diagnostics, optional):
//   - no session replay, no performance tracing (tracesSampleRate 0)
//   - no auto session tracking (session envelopes BYPASS beforeSend,
//     so they'd leak past the consent gate on a mid-session revoke)
//   - sendDefaultPii false (no user.id/email/username auto-capture)
//   - beforeSend consent hard-gate + IP strip + breadcrumb scrub
// Dynamically imported so the native SDK never enters the web bundle.
// `initialised` is flipped synchronously BEFORE the await to make the
// double-init race (two consent effects in one commit) impossible; it
// reverts on failure so a later grant can retry.
// Kept ONLY for pause/resumeAppHangTracking on consent flips — never
// for close() (see the lifecycle comment below).
let nativeSdkModule = null;

async function initNativeSentry() {
  try {
    const SentryCapacitor = await import('@sentry/capacitor');
    // Revoked while the import was in flight — don't start the SDK at
    // all. The next grant re-enters via the initialised=false reset.
    if (!nativeConsentGranted) { initialised = false; return; }

    // Read release/dist off the shipped binary — the only place the real
    // per-platform version exists (see the SENTRY_WEB_RELEASE comment).
    //
    // On failure we OMIT both keys instead of substituting a placeholder. The
    // bridge only calls setRelease/setDist when the key is actually present
    // (SentryCapacitor.java:104-110 guards on capOptions.has(...)), so leaving
    // them out lets sentry-android / sentry-cocoa auto-derive the release from
    // the package itself — `com.mariusnpop.petzy@1.0.2+4`. Correct, just named
    // differently, and loud about it on the Releases page. A placeholder would
    // be the one bad outcome: plausible-looking and silently back to the
    // never-re-opens bug.
    let versionOptions = {};
    try {
      const { App } = await import('@capacitor/app');
      const info = await App.getInfo();
      versionOptions = {
        release: `petzy-${isBusinessApp ? 'business' : 'client'}@${info.version}+${info.build}`,
        dist: String(info.build),
      };
    } catch { /* omit both — native auto-detects, per above */ }

    // Second gate: consent can be revoked during EITHER await, and the one
    // above only covered the import.
    if (!nativeConsentGranted) { initialised = false; return; }

    SentryCapacitor.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      // Spread, not fixed keys: absent means "auto-detect", and an explicit
      // `release: undefined` would NOT — the bridge's has() check would still
      // see the key.
      ...versionOptions,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      enableAutoSessionTracking: false,
      // Outcome envelopes ("N events dropped") would otherwise keep
      // flowing to Sentry after a revoke — the gate drops events, and
      // the client-report counter would report exactly that, forever
      // (the native client is never closed). No data may leave the
      // device post-revoke, so reports are off.
      sendClientReports: false,
      beforeSend: nativeBeforeSend,
    }, Sentry.init);
    nativeSdkModule = SentryCapacitor;
    console.info('[Sentry] native SDK initialised after Analize consent granted');
  } catch (err) {
    initialised = false;
    console.warn('[Sentry] native init failed:', err);
  }
}

// Push the current analytics-consent state into Sentry. Grants → init,
// revoke → close + storage wipe. Re-granting after a revoke re-inits
// cleanly: the SDK replaces the global hub's client.
//
// NATIVE lifecycle is deliberately "init once, gate forever" — we NEVER
// call SentryCapacitor.close(). Verified against @sentry/capacitor
// 4.2.0 internals: close() flips a module-level one-way latch
// (NATIVE.enableNative=false, wrapper.js) that nothing ever resets, so
// a close→re-init leaves a client wired to a permanently-disabled
// native transport — every JS event silently dropped until app restart.
// close() during the async bridge-init window is also a no-op (no
// client yet), leaving the SDK running with no teardown. Instead:
//   - grant   → init once; re-grant just re-opens the beforeSend gate.
//   - revoke  → nativeConsentGranted=false: beforeSend drops every
//               event pre-network; sessions are off at init; the armed
//               native crash handler only persists a crash locally —
//               it is transmitted on a LATER launch only if consent is
//               granted then (revoked consent at launch = SDK never
//               starts, nothing is sent).
export function syncConsentToSentry({ analytics }) {
  if (!isSentryConfigured()) return;

  // ── Native app (Capacitor): consent-gated crash/diagnostics only ──
  if (isNativeApp) {
    if (analytics) {
      nativeConsentGranted = true;
      if (!initialised) {
        initialised = true;
        initNativeSentry();
      } else {
        // Re-grant: reopen the iOS AppHang channel we paused on revoke
        // (idempotent; no-op on Android / before the SDK ever started).
        try { nativeSdkModule?.resumeAppHangTracking?.(); } catch { /* bridge unavailable */ }
      }
      return;
    }
    nativeConsentGranted = false;
    // iOS AppHang events are captured AND sent entirely in the native
    // layer (sentry-cocoa enableAppHangTracking defaults true and the
    // Capacitor plugin doesn't map the option), so they'd bypass the JS
    // beforeSend gate mid-session. Pause the tracker on revoke — the
    // only native-side channel that can emit while the app is running.
    try { nativeSdkModule?.pauseAppHangTracking?.(); } catch { /* bridge unavailable */ }
    clearSentryStorage();
    return;
  }

  if (analytics && !initialised) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: SENTRY_ENVIRONMENT,
      // petzy-web@<sha>, not petzy-client@<semver>: the web app and the native
      // client are different artifacts on different clocks, and sharing one
      // release name merged them. This is also the ONE path where release
      // health actually runs (browserSessionIntegration is a @sentry/browser
      // default and the integrations array below merges with the defaults
      // rather than replacing them), so it was the most-polluted data we own.
      release: SENTRY_WEB_RELEASE,
      // No `dist`: it identifies a build WITHIN a release, and a web deploy has
      // exactly one. It used to carry 'client'/'business' — product identity,
      // which belongs in (and is already in) the release name.
      // Explicit (matches the native config): never auto-attach
      // user.id/email/username/IP to events.
      sendDefaultPii: false,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Traces + replays sample at 10% to keep the event budget
      // reasonable; bump tracesSampleRate temporarily when hunting a
      // specific perf regression, then lower it again.
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      // Always replay an errored session — cheap and invaluable when a
      // user hits a bug we can't reproduce.
      replaysOnErrorSampleRate: 1.0,
    });
    initialised = true;
    console.info('[Sentry] initialised after Analize consent granted');
    return;
  }

  if (!analytics) {
    if (initialised) {
      // Sentry.close() returns a Promise — flushes pending events,
      // disables the client, and may persist final state along the
      // way. We clear storage immediately for the synchronous Cookiebot
      // case (the scanner reads cookies right after the consent click)
      // AND chain a second clear on the close promise so any final
      // SDK write during flush gets swept too.
      //
      // Race guard: if the user toggles revoke → grant fast enough,
      // syncConsentToSentry runs the grant arm before the close promise
      // resolves, flipping `initialised` back to true. The post-close
      // sweep then has to NOT wipe the freshly-initialised session's
      // storage. Reading the live `initialised` value at resolve-time
      // gives us that — no captured snapshot, the current state wins.
      const postClose = () => { if (!initialised) clearSentryStorage(); };
      Sentry.close().then(postClose, postClose);
      initialised = false;
      console.info('[Sentry] closed after Analize consent withdrawn');
    }
    // Always clear storage on the no-consent path — covers revoke
    // (matches clearGaCookies pattern in gtag.js), first-paint
    // pre-consent (sweeps anything a previous session left behind),
    // and the new decidedAt-gated path where the React state can be
    // false even though the localStorage record carries true.
    clearSentryStorage();
  }
}
